import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { CheckCircle2Icon, FileTextIcon, HelpCircleIcon, MailIcon, PauseCircleIcon } from 'lucide-react';
import { HERO_REVIEW } from '../../data/exampleReviews';
import { cn } from '../../src/lib/utils';
const ROWS = [{
  key: 'requirement',
  icon: CheckCircle2Icon,
  tone: 'text-lime bg-violet-soft'
}, {
  key: 'document',
  icon: FileTextIcon,
  tone: 'text-[#1E7A46] bg-[#E8F6EE]'
}, {
  key: 'question',
  icon: HelpCircleIcon,
  tone: 'text-[#9A5B13] bg-[#FDF3E7]'
}, {
  key: 'followUp',
  icon: MailIcon,
  tone: 'text-[#B42318] bg-[#FDECEC]'
}] as const;

/** Homepage hero visual: one requirement traced to evidence, an open question and the follow-up. */
export function HeroReview() {
  const reduceMotion = useReducedMotion();
  return <figure aria-label={`Example review of ${HERO_REVIEW.company}`} className="overflow-hidden rounded-2xl border border-white/10 bg-panel text-left shadow-[0_50px_120px_-50px_rgba(124,92,255,0.55)]" data-id="element-541" data-mp="1r9329hxv3opc">
      <div className="flex items-center justify-between gap-3 border-b border-hairline bg-surface px-4 py-3 sm:px-5" data-id="element-542" data-mp="zagrtz6xy4j5">
        <div className="min-w-0" data-id="element-543" data-mp="143t2f5escasw">
          <p className="truncate text-sm font-semibold text-text-primary" data-id="element-544" data-mp="qatqfde7oyq3">{HERO_REVIEW.company}</p>
          <p className="truncate text-xs text-text-tertiary" data-id="element-545" data-mp="v9xxnyhksmds">{HERO_REVIEW.context}</p>
        </div>
        <span className="shrink-0 rounded-md bg-[#FDF3E7] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#9A5B13]" data-id="element-546" data-mp="1iziv1meh7m6l">
          Example review
        </span>
      </div>

      <ol className="relative flex flex-col gap-0 p-4 sm:p-5" data-id="element-547" data-mp="1myrmgkm1n7yz">
        {ROWS.map((row, index) => {
        const item = HERO_REVIEW[row.key];
        const Icon = row.icon;
        return <motion.li key={row.key} {...reduceMotion ? {} : {
          initial: {
            opacity: 0,
            y: 8
          },
          animate: {
            opacity: 1,
            y: 0
          },
          transition: {
            duration: 0.45,
            delay: 0.35 + index * 0.18,
            ease: [0.22, 1, 0.36, 1] as [number, number, number, number]
          }
        }} className="relative flex gap-3 pb-4 last:pb-0" data-id="element-548" data-mp="1e28ylmso6a99" data-mpi={index}>
              {index < ROWS.length - 1 ? <span aria-hidden="true" className="absolute left-[15px] top-9 h-[calc(100%-28px)] w-px bg-hairline" data-id="element-549" data-mp="30x5eczf76vs9" /> : null}
              <span className={cn('relative flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', row.tone)} data-id="element-550" data-mp="2vz3z6pkw7gm2">
                <Icon className="h-4 w-4" aria-hidden="true" data-id="element-551" data-mp="2qxmwqpmrfm3d" />
              </span>
              <div className="min-w-0 flex-1 rounded-xl border border-hairline-soft bg-surface px-3.5 py-2.5" data-id="element-552" data-mp="2my5rnxb8wqyq">
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-tertiary" data-id="element-553" data-mp="2hzuhflh1gefz">{item.label}</p>
                <p className="mt-0.5 text-[14px] leading-snug text-text-primary" data-id="element-554" data-mp="l3wxs9fer4qd">{item.text}</p>
                {'source' in item ? <p className="mt-1 font-mono text-[11px] text-text-tertiary" data-id="element-555" data-mp="nqfi4pu4sxhv">{item.source}</p> : null}
                {'status' in item ? <p className="mt-1.5 inline-flex rounded-md bg-violet-soft px-1.5 py-0.5 text-[11px] font-semibold text-lime" data-id="element-556" data-mp="soh4pkqrney3">{item.status}</p> : null}
              </div>
            </motion.li>;
      })}
      </ol>

      <div className="flex items-center gap-2.5 border-t border-hairline bg-[#FAFAFC] px-4 py-3 sm:px-5" data-id="element-557" data-mp="12h4xm4emkyrj">
        <PauseCircleIcon className="h-4 w-4 shrink-0 text-[#9A5B13]" aria-hidden="true" data-id="element-558" data-mp="4twe2ey6pzs6" />
        <p className="text-[13px] font-medium text-text-primary" data-id="element-559" data-mp="9shiyrodt7my">{HERO_REVIEW.decision}</p>
      </div>
    </figure>;
}